import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { LinesData } from 'store/slices/RoofToolLinesSlice/types';
import { selectHasCustomMapChanges } from './customMapSelector';
import { selectHasTreeEdits } from 'store/slices/TreeSlice/selector';
import { createInitialSelectedFacetVal } from 'store/slices/RoofToolLinesSlice/util';

enum TOOL_ACTION {
	// -------- Facets -----------
	EDIT_FACET = 'EDIT_FACET',
	DRAW_FACET = 'DRAW_MODE',
	DELETE_FACET = 'DELETE_FACET',
	EDIT_EDGES = 'EDIT_EDGES',

	// Behaves like a mode (requires done/cancel) but isnt actual mode
	EDIT_AZIMUTH_OR_POSITION = 'EDIT_AZIMUTH_OR_POSITION',

	// Direct actions (no modes, no done/cancel)
	CHANGE_ORIENTATION = 'CHANGE_ORIENTATION',
	REMOVE_SHADING = 'REMOVE_SHADING',
	RECALCULATE_LAYOUT = 'RECALCULATE_LAYOUT',
	EDIT_CUSTOM_IMAGE = 'EDIT_CUSTOM_IMAGE',

	// obstructions
	EDIT_OBSTRUCTION = 'EDIT_OBSTRUCTION_MODE',

	// ----panel--------
	DELETE_PANEL = 'DELETE_PANEL',
	ADD_PANEL = 'ADD_PANEL',
	MOVE_PANEL = 'MOVE_PANEL',

	// ----trees--------
	EDIT_TREE = 'EDIT_TREE',
}
const DONE_CONFIRMATION_REQUIRED: TOOL_ACTION[] = [
	TOOL_ACTION.DELETE_FACET,
];

// direct actions are actions which doesnt have done and cancel 
const DIRECT_ACTIONS = [
	TOOL_ACTION.CHANGE_ORIENTATION,
	TOOL_ACTION.RECALCULATE_LAYOUT,
	TOOL_ACTION.REMOVE_SHADING,
	TOOL_ACTION.EDIT_CUSTOM_IMAGE
];

/**
 * "selectIsAnyToolActionActive"
 *
 * Returns TRUE if:
 *  - Any mode is active, OR
 *  - Any editing interaction is in progress (facet translation)
 *
 * Purpose:
 *  - Used to disable global interactions
 *  - Used to block switching modes while current work is ongoing
 */
const selectIsAnyToolActionActive = createSelector(
	[
		(state: RootState) => state.toolNewPostions.data,
		(state: RootState) => state.roofToolLines.data,
		(state: RootState) => state.roofEditData.data,
		(state: RootState) => state.panelSlice.data,
		(state: RootState) => state.customMap.data,
		(state: RootState) => state.treeSlice.data,
	],
	(toolNewPostions, roofToolLines, roofEditData, panelSlice, customMapSlice, treeSlice) => {
		return (

			// ---------facets--------------------
			toolNewPostions.drawModeEnabled ||
			toolNewPostions.deleteFacetMode ||
			!!toolNewPostions.roofIndexes.length || // checks if facet has been translated;

			roofToolLines.activeRoofTool ||
			roofEditData.enableEditFacet ||

			// --------- obstructions ----------
			toolNewPostions.enableEditObs ||

			//---------panels------------------
			panelSlice.enableAddPanel ||
			panelSlice.enablePanelMove ||
			panelSlice.deleteIndividualPanelMode ||

			//---------custom image-------------
			customMapSlice.isImageEditingEnabled &&
			customMapSlice.editingMapId != null ||

			//---------trees-------------
			treeSlice.treeEditingMode ||

			//---------map only-------------
			toolNewPostions.hideSatelliteOverlay
		);
	}
);

/**
 * "selectIsEditingModeEnabled"
 *
 * Returns TRUE only if a real "mode" is active.
 *
 * Definition of mode:
 *  - User manually toggles a mode button ("Edit", "Add Panel", "Draw", etc.)
 *  - Requires explicit Done/Cancel
 *
 * Does NOT include:
 *  - Direct actions
 *  - Non-mode actions like facet translation
 *
 * Purpose:
 *  - Useful for resetting tools when user leaves a mode
 *  - Drives the “mode toggle” UI state
 */
const selectIsEditingModeEnabled = createSelector(
	[
		(state: RootState) => state.toolNewPostions.data,
		(state: RootState) => state.roofToolLines.data,
		(state: RootState) => state.roofEditData.data,
		(state: RootState) => state.panelSlice.data,
		(state: RootState) => state.treeSlice.data,
	],
	(toolNewPostions, roofToolLines, roofEditData, panelSlice, treeSlice) => {
		return (

			// ---------facets--------------------
			toolNewPostions.drawModeEnabled ||
			toolNewPostions.deleteFacetMode ||

			roofToolLines.activeRoofTool ||
			roofEditData.enableEditFacet ||

			// --------- obstructions ----------
			toolNewPostions.enableEditObs||

			//---------panels------------------
			panelSlice.enableAddPanel ||
			panelSlice.enablePanelMove ||
			panelSlice.deleteIndividualPanelMode ||

			//---------trees-------------
			treeSlice.treeEditingMode
		);
	}
);

/**
 * "selectActiveAction"
 *
 * Determines the currently active TOOL_ACTION.
 *
 * Priority:
 *  1. Modes
 *  2. Direct actions (modal-based)
 *  3. Non-mode transient actions (translations)
 *
 * Returned value drives:
 *  - Which handler to run
 *  - Whether confirmation is needed
 *  - UI highlighting
 */
const selectActiveAction = createSelector(
	[
		(state: RootState) => state.toolNewPostions.data,
		(state: RootState) => state.roofToolLines.data,
		(state: RootState) => state.roofEditData.data,
		(state: RootState) => state.panelSlice.data,
		(state: RootState) => state.treeSlice.data,
		(state: RootState) => state.changeOrientationModal.data,
		(state: RootState) => state.editFacetModal.data,
		(state: RootState) => state.adjustShadingState.data.shadingMenu.open,
		(state: RootState) =>
			state.customMap.data.isImageEditingEnabled &&
			state.customMap.data.editingMapId !== -1,
	],
	(toolNewPostions, roofToolLines, roofEditData, panelSlice, treeSlice, changeOrientationModalSlice, 
		editFacetModalSlice, isShadingMenuOpen, isEditingCustomImage) => {
		// facets
		if (toolNewPostions.drawModeEnabled) return TOOL_ACTION.DRAW_FACET;
		if (toolNewPostions.deleteFacetMode) return TOOL_ACTION.DELETE_FACET;
		if (roofToolLines.activeRoofTool) return TOOL_ACTION.EDIT_EDGES;
		if (roofEditData.enableEditFacet) return TOOL_ACTION.EDIT_FACET;

		// obstructions
		if (toolNewPostions.enableEditObs) return TOOL_ACTION.EDIT_OBSTRUCTION;

		// trees
		if (treeSlice.treeEditingMode) return TOOL_ACTION.EDIT_TREE;

		// panel
		if (panelSlice.enableAddPanel) return TOOL_ACTION.ADD_PANEL;
		if (panelSlice.enablePanelMove) return TOOL_ACTION.MOVE_PANEL;
		if (panelSlice.deleteIndividualPanelMode) return TOOL_ACTION.DELETE_PANEL;

		// actions that have no modes
		if (isEditingCustomImage) return TOOL_ACTION.EDIT_CUSTOM_IMAGE;
		if (changeOrientationModalSlice.open) return TOOL_ACTION.CHANGE_ORIENTATION;
		if (editFacetModalSlice.recalculateLayoutModalOpen) return TOOL_ACTION.RECALCULATE_LAYOUT;
		if (isShadingMenuOpen) return TOOL_ACTION.REMOVE_SHADING;

		// neither mode nor a direct action
		//facet translations
		if (toolNewPostions.roofIndexes?.length) return TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION;
		return null;
	}
);

/**
 * cancelConfirmationMap
 *
 * For each mode-type action, defines:
 *  - Whether there are unsaved changes
 *
 * Used by:
 *  - Keyboard shortcuts
 *  - Cancel buttons
 *  - Mode switching logic
 *
 * NOT used for:
 *  - Direct actions (these have their own modal confirmation flow)
 */
const cancelConfirmationMap: Partial<Record<TOOL_ACTION, (state: RootState) => boolean>> = {
	[TOOL_ACTION.DELETE_FACET]: (state: RootState) =>
		state.toolNewPostions.data.deletedRoofIndexes.length > 0,

	[TOOL_ACTION.DRAW_FACET]: (state: RootState) =>
		state.toolNewPostions.data.drawnFacetExists,

	[TOOL_ACTION.EDIT_FACET]: (state: RootState) =>
		state.roofEditData.data.editableFacets.filter(seg => seg?.isEdited).length > 0,

	[TOOL_ACTION.ADD_PANEL]: (state: RootState) =>
		state.panelSlice.data.newlyAddedPanels.length > 0,

	[TOOL_ACTION.DELETE_PANEL]: (state: RootState) =>
		state.panelSlice.data.deletedPanels.length > 0,

	[TOOL_ACTION.MOVE_PANEL]: (state: RootState) =>
		state.panelSlice.data.currentMovablePanels.length > 0,

	[TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION]: (state: RootState) =>
		state.toolNewPostions.data.roofIndexes.length > 0,

	[TOOL_ACTION.EDIT_EDGES]: (state:RootState) => {
		const { selectedFacets, createSpecialEdges, updateSpecialEdges } = state.roofToolLines.data;
		const hasDetectedEdges = state.roofToolLines.autoEdgeDetection.confirmed;

		if(createSpecialEdges.length > 0 || updateSpecialEdges.length > 0 || hasDetectedEdges)	return true;
	
		for (const [_, { removedGutterLineIds, newAddedGutterLines }] of Object.entries(selectedFacets)) {
			if (newAddedGutterLines?.length) {
				return true;
			}
	
			if(removedGutterLineIds?.length) return true;
		}
		return false;
	},
	[TOOL_ACTION.EDIT_OBSTRUCTION]: (state: RootState) => state.toolNewPostions.data.drawnObsExists 
		|| state.toolNewPostions.data.deletedObstructionIDs.length > 0 
		|| Object.keys(state.toolNewPostions.data.editedObstructions ?? {}).length > 0,

	[TOOL_ACTION.EDIT_CUSTOM_IMAGE]: (state: RootState) => selectHasCustomMapChanges(state),
	[TOOL_ACTION.EDIT_TREE]: (state: RootState) => selectHasTreeEdits(state),

};


export const selectNeedCancelConfirmation = createSelector(
	[selectActiveAction, (state: RootState) => state],
	(activeAction, state) => (activeAction && cancelConfirmationMap[activeAction]?.(state)) || false
);
export const selectNeedDoneConfirmation = createSelector(
	[selectActiveAction, (state: RootState) => state],
	(activeAction, state) => {
		if (!activeAction) return false;

		const hasModification = cancelConfirmationMap[activeAction]?.(state) || false;

		if (DONE_CONFIRMATION_REQUIRED.includes(activeAction)) {
			return hasModification; 
		}
		return false;
	}
);

export const selectIsDoneButtonDisabled = (state: RootState) => {
	const action = selectActiveAction(state);
	if (!action) return true;
	const hasValidChanges = cancelConfirmationMap[action]?.(state);
	if (action === TOOL_ACTION.EDIT_OBSTRUCTION && state.toolNewPostions.data.invalidObstructionIds.length > 0) {
		return true;
	}

	return !hasValidChanges;
};

const isDirectAction = (action: TOOL_ACTION) => DIRECT_ACTIONS.includes(action);

const getSelectedFacets = createSelector([(state: RootState) => state.roofData.data],
	(roofData) => {
		const selectedFacets: LinesData['selectedFacets'] = {};
		for (const { type: lineType, edges, isSystemDetected } of roofData.specialEdges) {
			for (const [facetId, lineId] of Object.entries(edges)) {
				const facet = selectedFacets[facetId] ??= createInitialSelectedFacetVal();
				facet.selectedLines.push({ id: lineId, lineType, isSystemDetected });
			}
		}
		return selectedFacets;
	});

export {
	TOOL_ACTION,
	selectActiveAction,
	selectIsAnyToolActionActive,
	isDirectAction,
	selectIsEditingModeEnabled,
	getSelectedFacets,
};
